import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { RoomsApi } from '../../shared/api/rooms.api';

import { AlarmService, IReckonTelevisionService } from '@era/inroom-sdk';
import { MediaStateChangedEvent } from '../../shared/events/media-state-changed.event';
import { GuestCheckinModalComponent } from '../../sub-features/room-info/guest-checkin-modal/guest-checkin-modal.component';
import { AlarmEffectAlarmResetted, AlarmLocationModel } from '../../sub-features/room/alarm/alarm-store/alarm.actions';
import { selectAlarmIdFromAlarm } from '../../sub-features/room/alarm/alarm-store/alarm.reducer';
import { MediaEffectMediaResetted } from '../../sub-features/room/media/media.actions';
import { LocationActionTypes, LocationResolverLocationSelected } from '../locations/locations.actions';
import {
  RoomActionsUnion,
  RoomActionTypes,
  RoomAddModalConfirmed,
  RoomApiRoomAdded,
  RoomApiRoomDeleted,
  RoomApiRoomLoaded,
  RoomApiRoomsLoaded,
  RoomEffectNavigatedBack,
  RoomExtraDataUpdatedApi,
  RoomExtraDataUpdatedApiFailed,
  RoomGuestModalOpened,
  RoomHeaderRoomDeleted,
  RoomStatusRoomCheckedOut,
} from './rooms.actions';
import { selectRoomId } from './rooms.reducer';

@Injectable()
export class RoomEffects {
  private readonly router = inject(Router);
  private readonly actions$ = inject<Actions<RoomActionsUnion>>(Actions);
  private readonly roomApi = inject(RoomsApi);
  private readonly televisionService = inject(IReckonTelevisionService);
  private readonly alarmApi = inject(AlarmService);
  private readonly modalService = inject(NgbModal);

  resolveRoomsEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActionTypes.LOCATION_RESOLVER_LOCATION_SELECTED),
      switchMap((action: LocationResolverLocationSelected) => this.roomApi.getRooms(action.payload.HotelCode)),
      map((rooms) => new RoomApiRoomsLoaded(rooms))
    );
  });

  addRoomEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_ADD_MODAL_CONFIRMED),
      switchMap((action: RoomAddModalConfirmed) => {
        const { HotelCode, Room } = action.payload;
        if (!HotelCode || !Room) return EMPTY;
        return this.roomApi.addRoom(HotelCode, Room);
      }),
      map((room) => new RoomApiRoomAdded(room))
    );
  });

  deleteRoomEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_HEADER_ROOM_DELETED),
      switchMap((action: RoomHeaderRoomDeleted) => {
        const { HotelCode, Room } = action.payload;
        if (!HotelCode || !Room) return EMPTY;
        return this.roomApi.deleteRoom(HotelCode, Room).pipe(map(() => action));
      }),
      map((action: RoomHeaderRoomDeleted) => new RoomApiRoomDeleted(action.payload))
    );
  });

  guestCheckinEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_STATUS_ROOM_CHECKED_IN),
      map(() => {
        this.modalService.open(GuestCheckinModalComponent);
        return new RoomGuestModalOpened();
      })
    );
  });

  checkoutRoomResetMediaEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_STATUS_ROOM_CHECKED_OUT),
      switchMap((action: RoomStatusRoomCheckedOut) => {
        const { HotelCode, Room } = action.payload;
        if (!HotelCode || !Room) return EMPTY;
        return this.televisionService
          .powerMediaDevice({
            hotelCode: HotelCode,
            room: Room,
            powerMediaDeviceApiModel: { On: false },
          })
          .pipe(
            switchMap(() => this.televisionService.getMediaState({ hotelCode: HotelCode, room: Room })),
            map((apiRes) => {
              const mediaState = (apiRes.Data ?? [])[0] as MediaStateChangedEvent;
              mediaState.HotelCode = HotelCode;
              mediaState.UnitCode = Room;
              return new MediaEffectMediaResetted({ mediaState });
            })
          );
      })
    );
  });

  checkoutRoomResetAlarmEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_STATUS_ROOM_CHECKED_OUT),
      mergeMap((action: RoomStatusRoomCheckedOut) => {
        const { HotelCode, Room } = action.payload;
        if (!HotelCode || !Room) return EMPTY;
        return this.alarmApi
          .setAlarm({
            hotelCode: HotelCode,
            room: Room,
            alarmApiModel: { Room },
          })
          .pipe(
            switchMap(() => this.alarmApi.getAlarm({ hotelCode: HotelCode, room: Room })),
            map((apiResult) => {
              const alarm = apiResult.Data as AlarmLocationModel;
              alarm.hotelCode = HotelCode;
              return new AlarmEffectAlarmResetted({ id: selectAlarmIdFromAlarm(alarm), changes: alarm });
            })
          );
      })
    );
  });

  checkoutRoomEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_STATUS_ROOM_CHECKED_OUT),
      switchMap((action: RoomStatusRoomCheckedOut) => {
        const { HotelCode, Room } = action.payload;
        if (!HotelCode || !Room) return EMPTY;
        return this.roomApi.checkoutRoom(HotelCode, Room).pipe(
          switchMap(() => this.roomApi.getRoom(HotelCode, Room)),
          map((room) => new RoomApiRoomLoaded({ id: selectRoomId(room), changes: room }))
        );
      })
    );
  });

  deleteRoomNavigateEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_HEADER_ROOM_DELETED),
      switchMap(() => this.router.navigate(['./'])),
      map(() => new RoomEffectNavigatedBack())
    );
  });

  updateExtraDataEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_EXTRA_DATA_UPDATED),
      mergeMap((action) =>
        this.roomApi.updateExtraData(action.payload.hotelCode, action.payload.unitCode, action.payload.data).pipe(
          map(() => new RoomExtraDataUpdatedApi(action.payload)),
          catchError(() => of(new RoomExtraDataUpdatedApiFailed()))
        )
      )
    );
  });
}
